// ─────────────────────────────────────────────────────────────────────────────
// Voice Mode 2.0 — Phase P0 "Visible Spine": highlight overlay.
//
// Renders the agent's presence while a plan executes: pulsing highlight rings
// around the real targets, an agent pointer dot on the active step, a caption
// chip for the (single) spoken line, and a "SAIK is driving" chip.  The layer
// is strictly `pointer-events: none` — it can never intercept a click, so the
// app behaves exactly as if it were not there.
//
// Spec: docs/plans/wcp-agent-ui-control-and-animation_v1_2026-09-19.md §2
//       (ACTING blue-violet ring; result legibility) — reduced-motion and the
//       full animation system land with P3.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';

import { resolveAgentId } from './registry';
import { useUiControlEvents } from './useUiControlEvents';

interface HighlightBox {
  key: string;
  agentId: string;
  top: number;
  left: number;
  width: number;
  height: number;
  visible: boolean;
}

/** Recompute target rects (re-render while highlighting so the ring follows
 *  layout shifts, e.g. a list loading in). */
function useHighlightBoxes(highlights: { key: string; agentId: string }[]): HighlightBox[] {
  const [boxes, setBoxes] = useState<HighlightBox[]>([]);

  useEffect(() => {
    if (highlights.length === 0) {
      setBoxes([]);
      return;
    }
    let alive = true;
    let raf = 0;
    const recompute = () => {
      if (!alive) return;
      setBoxes(
        highlights.map((h) => {
          const el = resolveAgentId(h.agentId);
          const rect = el?.getBoundingClientRect();
          return rect
            ? {
                key: h.key,
                agentId: h.agentId,
                top: rect.top,
                left: rect.left,
                width: rect.width,
                height: rect.height,
                visible: rect.width > 0 && rect.height > 0,
              }
            : { key: h.key, agentId: h.agentId, top: 0, left: 0, width: 0, height: 0, visible: false };
        }),
      );
      raf = requestAnimationFrame(recompute);
    };
    recompute();
    return () => {
      alive = false;
      cancelAnimationFrame(raf);
    };
  }, [highlights]);

  return boxes;
}

export default function UiControlLayer() {
  const state = useUiControlEvents();
  const boxes = useHighlightBoxes(state.highlights);

  const active = boxes.length > 0 || state.phase === 'executing' || Boolean(state.caption);
  if (!active) return null; // zero render when idle — flag off = invisible

  const pointerBox = boxes.length > 0 ? boxes[boxes.length - 1] : null;

  return (
    <div
      className="pointer-events-none fixed inset-0 z-[80]"
      aria-hidden="true"
      data-ui-control-layer=""
      data-ui-phase={state.phase}
      data-ui-highlight-count={boxes.filter((box) => box.visible).length}
      data-ui-caption={state.caption ? 'set' : 'clear'}
    >
      {/* Highlight rings around the real targets */}
      {boxes
        .filter((box) => box.visible)
        .map((box) => (
          <div
            key={box.key}
            data-agent-ring={box.agentId}
            className="absolute animate-pulse rounded-xl"
            style={{
              top: box.top - 4,
              left: box.left - 4,
              width: box.width + 8,
              height: box.height + 8,
              border: '2px solid rgb(139 92 246 / 0.9)',
              boxShadow: '0 0 0 4px rgb(139 92 246 / 0.25), 0 0 24px 6px rgb(139 92 246 / 0.35)',
            }}
          >
            {/* Owner defect (2026-09-20): the SAIK chip sat inside the ring and
                covered the top edge of its own button. It now hangs OUTSIDE the
                ring (above it, or below it when the target hugs the viewport
                top) so it never covers the control it labels. */}
            <span
              data-agent-ring-badge={box.agentId}
              className={`pointer-events-none absolute right-0 rounded-md bg-violet-600 px-1.5 py-0.5 text-[10px] font-semibold text-white shadow ${
                box.top >= 34 ? 'bottom-full mb-1' : 'top-full mt-1'
              }`}
            >
              SAIK
            </span>
          </div>
        ))}

      {/* Agent pointer dot on the most recent target (WCP §2.2 presence) */}
      {pointerBox?.visible && (
        <div
          className="absolute h-3.5 w-3.5 rounded-full bg-violet-500 shadow-[0_0_12px_4px_rgba(139,92,246,0.6)] transition-all duration-300"
          style={{
            top: pointerBox.top + pointerBox.height / 2 - 7,
            left: pointerBox.left + pointerBox.width / 2 - 7,
          }}
        />
      )}

      {/* Driving chip */}
      {state.phase === 'executing' && (
        <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-2 rounded-full bg-slate-900/90 px-4 py-2 text-xs font-medium text-white shadow-lg dark:bg-slate-800/95">
          <span className="inline-block h-2 w-2 animate-pulse rounded-full bg-violet-400" />
          SAIK is driving the UI…
        </div>
      )}

      {/* Caption: the only spoken line (also announced to screen readers) */}
      {state.caption && (
        <div
          role="status"
          aria-live="polite"
          className="absolute bottom-16 left-1/2 -translate-x-1/2 rounded-xl bg-slate-900/90 px-4 py-2 text-sm text-white shadow-lg dark:bg-slate-800/95"
        >
          {state.caption}
        </div>
      )}
    </div>
  );
}